import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UtensilsCrossed, FileCheck2, Landmark, Boxes, ShoppingBag, Target,
  TrendingUp, Users, Factory, Truck, FolderKanban, Calculator,
  ShieldCheck, Fingerprint, DatabaseBackup, Globe, ScrollText, LockKeyhole,
  ArrowRight, CheckCircle2, Sparkles, Building2, CalendarDays, BarChart3,
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const MODULES_MARQUEE = [
  "POS & Restauração", "Faturação AGT / SAF-T", "Stock & Lotes", "Compras & Cotações",
  "CRM & Pipeline", "Financeiro & Bancos", "Contabilidade", "RH & Salários",
  "Produção (BOM)", "Logística & Frota", "Projetos", "BI Executivo",
  "Multi-empresa", "14 Cargos RBAC", "Backups Automáticos", "2FA",
];

const IMG_POS = "https://images.unsplash.com/photo-1556745750-68295fefafc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwY2hlZnxlbnwwfHx8fDE3ODc0MTMwNTJ8MA&ixlib=rb-4.1.0&q=85";
const IMG_STOCK = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzg3NDEzMDUxfDA&ixlib=rb-4.1.0&q=85";
const IMG_BI = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3ODczOTU1OTd8MA&ixlib=rb-4.1.0&q=85";

const BentoCard = ({ className = "", children, testid }) => (
  <motion.div
    {...fadeUp}
    data-testid={testid}
    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors duration-300 hover:border-cyan-400/40 hover:bg-white/[0.06] ${className}`}
  >
    {children}
  </motion.div>
);

const FeatureIcon = ({ icon: Icon }) => (
  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-cyan-300">
    <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
  </div>
);

export default function LandingPage() {
  const [cycle, setCycle] = useState("monthly");
  const fmt = (v) => v.toLocaleString("pt-AO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white antialiased" style={{ fontFamily: "'Satoshi', Inter, sans-serif" }} data-testid="landing-page">
      <style>{`
        @keyframes lumora-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes lumora-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .lumora-heading { font-family: 'Cabinet Grotesk', 'Outfit', Inter, sans-serif; }
        ::selection { background: rgba(0,240,255,0.25); }
      `}</style>

      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/50 backdrop-blur-xl" data-testid="landing-header">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <a href="#top" className="lumora-heading flex items-center gap-2.5 text-xl font-extrabold tracking-tight" data-testid="landing-logo">
            <img src="/lumora-logo.png" alt="Lumora" className="h-8 w-8" />
            LUMORA
          </a>
          <nav className="hidden items-center gap-8 text-sm text-zinc-400 md:flex" aria-label="Navegação principal">
            <a href="#funcionalidades" className="transition-colors duration-200 hover:text-white" data-testid="nav-funcionalidades">Funcionalidades</a>
            <a href="#seguranca" className="transition-colors duration-200 hover:text-white" data-testid="nav-seguranca">Segurança</a>
            <a href="#precos" className="transition-colors duration-200 hover:text-white" data-testid="nav-precos">Preços</a>
            <a href="#numeros" className="transition-colors duration-200 hover:text-white" data-testid="nav-numeros">O Sistema</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="rounded-full px-4 py-2 text-sm text-zinc-300 transition-colors duration-200 hover:text-white" data-testid="header-login-btn">Entrar</Link>
            <Link to="/login" className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5" data-testid="header-register-btn">
              Criar Conta
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden pt-36 pb-24">
        <div className="pointer-events-none absolute -top-40 right-0 h-[560px] w-[560px] rounded-full bg-cyan-500/10 blur-[140px]" />
        <div className="pointer-events-none absolute top-64 -left-40 h-[400px] w-[400px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-[1.1fr_0.9fr]">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-cyan-300" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> ERP + POS · Feito para Angola
            </p>
            <h1 className="lumora-heading text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="hero-title">
              O Sistema<br />Operativo para<br />
              <span className="bg-gradient-to-r from-cyan-300 to-blue-500 bg-clip-text text-transparent">Empresas Angolanas</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-400" data-testid="hero-subtitle">
              Da mesa do restaurante ao balancete contabilístico. POS, faturação SAF-T (AGT),
              stock, compras, RH, finanças, produção e logística — tudo num só lugar, 100% em Kwanza.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/login" className="group inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 text-base font-bold text-black transition-transform duration-200 hover:-translate-y-0.5" data-testid="hero-cta-button">
                Começar Gratuitamente
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
              <a href="#funcionalidades" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-base font-medium text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white" data-testid="hero-secondary-button">
                Ver Funcionalidades
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500">
              {["Sem cartão de crédito", "Multi-empresa", "Registo em 2 minutos"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-cyan-400" aria-hidden="true" /> {t}</span>
              ))}
            </div>
          </motion.div>

          {/* Floating dashboard mock */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block"
            aria-hidden="true"
          >
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl" style={{ animation: "lumora-float 7s ease-in-out infinite" }}>
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Vendas de Hoje</p>
                  <p className="lumora-heading mt-1 text-3xl font-extrabold">1.240.500 <span className="text-lg text-zinc-400">Kz</span></p>
                </div>
                <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-sm font-semibold text-emerald-400">+12,4%</span>
              </div>
              <div className="mt-5 flex h-24 items-end gap-2">
                {[38, 55, 42, 70, 62, 88, 76, 95, 68, 100, 84, 92].map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-blue-600/60 to-cyan-400/80" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                  <p className="text-xs text-zinc-500">Stock crítico</p>
                  <p className="mt-1 text-lg font-bold text-amber-400">3 artigos</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/30 p-3">
                  <p className="text-xs text-zinc-500">Contas a receber</p>
                  <p className="mt-1 text-lg font-bold">845.000 Kz</p>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-8 rounded-xl border border-white/10 bg-black/70 px-4 py-3 backdrop-blur-xl" style={{ animation: "lumora-float 6s ease-in-out 1s infinite" }}>
              <p className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4 text-cyan-400" /> Fatura FR 2026/00321 emitida
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-white/10 bg-black/40 py-5 overflow-hidden" data-testid="modules-marquee">
        <div className="flex w-max gap-10 whitespace-nowrap" style={{ animation: "lumora-marquee 45s linear infinite" }}>
          {[...MODULES_MARQUEE, ...MODULES_MARQUEE].map((m, i) => (
            <span key={i} className="text-sm uppercase tracking-[0.25em] text-zinc-500 transition-colors duration-200 hover:text-white">{m} <span className="ml-8 text-cyan-500/60">·</span></span>
          ))}
        </div>
      </div>

      {/* Bento features */}
      <section id="funcionalidades" className="mx-auto max-w-7xl px-6 py-28">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="text-sm uppercase tracking-[0.25em] text-cyan-400">Funcionalidades</p>
          <h2 className="lumora-heading mt-3 text-3xl font-bold tracking-tight md:text-5xl">Um ERP completo.<br />Do balcão à contabilidade.</h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-4">
          {/* POS grande com imagem */}
          <BentoCard className="md:col-span-2 lg:row-span-2 min-h-[340px] !p-0" testid="feature-pos">
            <img src={IMG_POS} alt="Restaurante moderno a usar o POS Lumora" className="absolute inset-0 h-full w-full object-cover opacity-50 transition-opacity duration-300 group-hover:opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="relative flex h-full flex-col justify-end p-7">
              <FeatureIcon icon={UtensilsCrossed} />
              <h3 className="lumora-heading text-2xl font-bold">POS & Restauração</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-zinc-300">
                Ponto de venda rápido com mesas, ecrã de cozinha em tempo real, reservas,
                pedidos com baixa automática de stock e fecho de caixa por sessão.
              </p>
            </div>
          </BentoCard>

          <BentoCard testid="feature-invoicing">
            <FeatureIcon icon={FileCheck2} />
            <h3 className="lumora-heading text-lg font-bold">Faturação Certificada</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">FT, FR e Notas de Crédito com QR, IVA por produto, séries de documentos e exportação SAF-T AO para a AGT.</p>
          </BentoCard>

          <BentoCard className="lg:row-span-2 min-h-[280px] !p-0" testid="feature-stock">
            <img src={IMG_STOCK} alt="Armazém com gestão de stock Lumora" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-opacity duration-300 group-hover:opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
            <div className="relative flex h-full flex-col justify-end p-6">
              <FeatureIcon icon={Boxes} />
              <h3 className="lumora-heading text-lg font-bold">Stock, Lotes & Armazéns</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-300">Inventário, valorização, lotes com validade, alertas de rutura e transferências entre armazéns.</p>
            </div>
          </BentoCard>

          <BentoCard testid="feature-finance">
            <FeatureIcon icon={Landmark} />
            <h3 className="lumora-heading text-lg font-bold">Financeiro & Bancos</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Caixa, bancos, contas a pagar e receber, conta corrente de clientes com limite de crédito.</p>
          </BentoCard>

          <BentoCard testid="feature-purchases">
            <FeatureIcon icon={ShoppingBag} />
            <h3 className="lumora-heading text-lg font-bold">Compras & Fornecedores</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Requisições, cotações com comparação de preços, aprovações e avaliação de fornecedores A/B/C.</p>
          </BentoCard>

          <BentoCard testid="feature-crm">
            <FeatureIcon icon={Target} />
            <h3 className="lumora-heading text-lg font-bold">CRM & Vendas</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Leads, pipeline de oportunidades, orçamentos, promoções e tabelas de preços.</p>
          </BentoCard>

          <BentoCard className="md:col-span-2 min-h-[220px] !p-0" testid="feature-bi">
            <img src={IMG_BI} alt="Dashboard executivo de BI Lumora" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-opacity duration-300 group-hover:opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
            <div className="relative flex h-full max-w-sm flex-col justify-center p-7">
              <FeatureIcon icon={TrendingUp} />
              <h3 className="lumora-heading text-xl font-bold">BI & Dashboard Executivo</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-300">Vendas vs mês anterior, top produtos, saldo bancário, valor de stock e pipeline — em tempo real.</p>
            </div>
          </BentoCard>

          <BentoCard testid="feature-hr">
            <FeatureIcon icon={Users} />
            <h3 className="lumora-heading text-lg font-bold">RH & Salários</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Folhas de salário, recibos, presenças, férias e comissões de vendedores.</p>
          </BentoCard>

          <BentoCard testid="feature-accounting">
            <FeatureIcon icon={Calculator} />
            <h3 className="lumora-heading text-lg font-bold">Contabilidade</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Lançamentos, diários, balancete e demonstração de resultados (DRE) automáticos.</p>
          </BentoCard>

          <BentoCard testid="feature-production">
            <FeatureIcon icon={Factory} />
            <h3 className="lumora-heading text-lg font-bold">Produção</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Fichas técnicas (BOM) com custo e margem, ordens de fabrico com consumo automático.</p>
          </BentoCard>

          <BentoCard testid="feature-logistics">
            <FeatureIcon icon={Truck} />
            <h3 className="lumora-heading text-lg font-bold">Logística & Frota</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Guias de entrega, picking e expedição, veículos, combustível e manutenções.</p>
          </BentoCard>

          <BentoCard testid="feature-projects">
            <FeatureIcon icon={FolderKanban} />
            <h3 className="lumora-heading text-lg font-bold">Projetos</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Orçamentos, custos, receitas e rentabilidade por projeto.</p>
          </BentoCard>

          <BentoCard testid="feature-multicompany">
            <FeatureIcon icon={Building2} />
            <h3 className="lumora-heading text-lg font-bold">Multi-empresa & Filiais</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Várias empresas e filiais com dados totalmente isolados, exercícios fiscais e séries próprias.</p>
          </BentoCard>

          <BentoCard testid="feature-reservations">
            <FeatureIcon icon={CalendarDays} />
            <h3 className="lumora-heading text-lg font-bold">Reservas & Mesas</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Mapa de mesas em tempo real, reservas e menu digital com QR code por mesa.</p>
          </BentoCard>

          <BentoCard testid="feature-reports">
            <FeatureIcon icon={BarChart3} />
            <h3 className="lumora-heading text-lg font-bold">Relatórios & Exportação</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">Relatórios de vendas, lucro e performance com exportação para Excel.</p>
          </BentoCard>
        </div>
      </section>

      {/* Segurança */}
      <section id="seguranca" className="relative border-y border-white/10 bg-black/40 py-28">
        <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[600px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-[120px]" />
        <div className="mx-auto max-w-7xl px-6">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-sm uppercase tracking-[0.25em] text-cyan-400">Segurança Empresarial</p>
            <h2 className="lumora-heading mt-3 text-3xl font-bold tracking-tight md:text-5xl">Os seus dados,<br />protegidos como num banco.</h2>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Fingerprint, title: "Autenticação 2FA", desc: "Código de 6 dígitos por app autenticadora em cada login." },
              { icon: ShieldCheck, title: "14 Cargos & Permissões", desc: "Cada funcionário vê apenas os módulos do seu cargo (RBAC)." },
              { icon: DatabaseBackup, title: "Backups Automáticos", desc: "Cópia diária dos dados, download e restauro de emergência." },
              { icon: Globe, title: "Controlo de IP", desc: "Restrinja o acesso apenas aos IPs autorizados da empresa." },
              { icon: ScrollText, title: "Auditoria Completa", desc: "Histórico de todas as alterações: quem fez, o quê e quando." },
              { icon: LockKeyhole, title: "Encriptação & Políticas", desc: "Passwords com bcrypt, políticas configuráveis e bloqueio anti força-bruta." },
            ].map((f) => (
              <BentoCard key={f.title} testid={`security-${f.title.split(" ")[0].toLowerCase()}`}>
                <FeatureIcon icon={f.icon} />
                <h3 className="lumora-heading text-lg font-bold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.desc}</p>
              </BentoCard>
            ))}
          </div>
        </div>
      </section>

      {/* Preços */}
      <section id="precos" className="mx-auto max-w-7xl px-6 py-28">
        <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
          <p className="text-sm uppercase tracking-[0.25em] text-cyan-400">Preços</p>
          <h2 className="lumora-heading mt-3 text-3xl font-bold tracking-tight md:text-5xl">Um plano para cada negócio.</h2>
          <p className="mt-4 text-zinc-400">Comece com 14 dias de teste grátis. Sem cartão de crédito.</p>
          <div className="mt-8 inline-flex rounded-full border border-white/10 bg-black/40 p-1">
            <button
              onClick={() => setCycle("monthly")}
              className={`rounded-full px-6 py-2 text-sm font-semibold transition-colors duration-200 ${cycle === "monthly" ? "bg-cyan-400 text-black" : "text-zinc-400 hover:text-white"}`}
              data-testid="pricing-monthly-btn"
            >
              Mensal
            </button>
            <button
              onClick={() => setCycle("annual")}
              className={`rounded-full px-6 py-2 text-sm font-semibold transition-colors duration-200 ${cycle === "annual" ? "bg-cyan-400 text-black" : "text-zinc-400 hover:text-white"}`}
              data-testid="pricing-annual-btn"
            >
              Anual <span className="ml-1 text-xs opacity-80">−20%</span>
            </button>
          </div>
        </motion.div>

        <div className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-2">
          {/* Bronze */}
          <motion.div {...fadeUp} className="rounded-2xl border border-white/10 bg-white/[0.03] p-8" data-testid="pricing-card-bronze">
            <p className="lumora-heading text-xl font-bold">LUMORA <span className="text-amber-600">Bronze</span></p>
            <p className="mt-1 text-sm text-zinc-500">Para bares, restaurantes e cafés que precisam apenas de POS</p>
            <p className="mt-6">
              <span className="lumora-heading text-4xl font-black">{fmt(cycle === "annual" ? 7199.2 : 8999)} Kz</span>
              <span className="text-sm text-zinc-500"> /mês</span>
            </p>
            {cycle === "annual" && <p className="mt-1 text-xs text-cyan-400">Faturado anualmente: {fmt(86390.4)} Kz — poupa 20%</p>}
            <ul className="mt-6 space-y-2.5 text-sm text-zinc-300">
              {[
                "Venda, faturação e cobrança (POS)",
                "Emissão de faturas, recibos e proformas",
                "Fecho de caixa e relatórios de vendas",
                "Configuração de séries de faturas",
                "Registo e gestão de clientes",
                "Análise de faturação e conta corrente",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {f}</li>
              ))}
              <li className="pl-6 text-xs text-zinc-500">+3 mais funcionalidades</li>
            </ul>
            <Link
              to={`/login?plan=bronze&cycle=${cycle}`}
              className="mt-8 block rounded-full border border-white/20 py-3 text-center font-semibold text-white transition-colors duration-200 hover:border-cyan-400/60 hover:text-cyan-300"
              data-testid="pricing-cta-bronze"
            >
              Começar com o Bronze
            </Link>
          </motion.div>

          {/* Ouro */}
          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="relative rounded-2xl border border-cyan-400/40 bg-cyan-400/[0.05] p-8 shadow-[0_0_60px_rgba(0,240,255,0.08)]" data-testid="pricing-card-ouro">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-cyan-400 px-4 py-1 text-xs font-bold uppercase tracking-wider text-black">Mais Completo</span>
            <p className="lumora-heading text-xl font-bold">LUMORA <span className="text-cyan-300">Ouro</span></p>
            <p className="mt-1 text-sm text-zinc-400">ERP completo para empresas que querem tudo</p>
            <p className="mt-6">
              <span className="lumora-heading text-4xl font-black">{fmt(cycle === "annual" ? 63999.99 : 79999.99)} Kz</span>
              <span className="text-sm text-zinc-500"> /mês</span>
            </p>
            {cycle === "annual" && <p className="mt-1 text-xs text-cyan-400">Faturado anualmente: {fmt(767999.9)} Kz — poupa 20%</p>}
            <ul className="mt-6 space-y-2.5 text-sm text-zinc-200">
              {[
                "Todas as funcionalidades do plano Bronze",
                "Gestão de recorrências e faturação automatizada",
                "Personalização de publicidade no ecrã",
                "Associação de clientes a seguradoras/convénios",
                "Gestão de cartões de consumo e gift cards",
                "Gestão de seguradoras e relatórios financeiros",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {f}</li>
              ))}
              <li className="pl-6 text-xs text-zinc-500">+10 mais funcionalidades (compras, contabilidade, RH, produção, logística, BI...)</li>
            </ul>
            <Link
              to={`/login?plan=ouro&cycle=${cycle}`}
              className="mt-8 block rounded-full bg-cyan-400 py-3 text-center font-bold text-black transition-transform duration-200 hover:-translate-y-0.5"
              data-testid="pricing-cta-ouro"
            >
              Começar com o Ouro
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Números */}
      <section id="numeros" className="mx-auto max-w-7xl px-6 py-24">
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
          {[
            { n: "40+", l: "Módulos ERP integrados" },
            { n: "14", l: "Cargos com permissões" },
            { n: "SAF-T", l: "Exportação fiscal AGT" },
            { n: "100%", l: "Em Kwanza e Português" },
          ].map((s, i) => (
            <motion.div key={s.l} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="border-l border-white/10 pl-6" data-testid={`stat-${i}`}>
              <p className="lumora-heading text-4xl font-black tracking-tight text-cyan-300 md:text-5xl">{s.n}</p>
              <p className="mt-2 text-sm text-zinc-400">{s.l}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/10 py-32">
        <div className="pointer-events-none absolute bottom-0 left-1/2 h-80 w-[700px] -translate-x-1/2 rounded-full bg-cyan-500/15 blur-[130px]" />
        <motion.div {...fadeUp} className="relative mx-auto max-w-4xl px-6 text-center">
          <h2 className="lumora-heading text-4xl font-black tracking-tighter md:text-6xl" data-testid="cta-title">
            Pronto para modernizar<br />o seu negócio?
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-zinc-400">
            Crie a sua empresa no Lumora em 2 minutos. Sem instalação, sem cartão de crédito.
          </p>
          <Link to="/login" className="group mt-10 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-9 py-4 text-lg font-bold text-black transition-transform duration-200 hover:-translate-y-0.5" data-testid="cta-register-button">
            Criar Conta Grátis
            <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-black/60 py-14" data-testid="landing-footer">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 md:grid-cols-4">
          <div>
            <p className="lumora-heading flex items-center gap-2.5 text-xl font-extrabold"><img src="/lumora-logo.png" alt="Lumora" className="h-8 w-8" />LUMORA</p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-zinc-500">O sistema de gestão completo para restaurantes e empresas em Angola.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Produto</p>
            <ul className="mt-4 space-y-2 text-sm text-zinc-400">
              <li><a href="#funcionalidades" className="transition-colors hover:text-white">Funcionalidades</a></li>
              <li><a href="#seguranca" className="transition-colors hover:text-white">Segurança</a></li>
              <li><Link to="/login" className="transition-colors hover:text-white">Entrar</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Empresa</p>
            <ul className="mt-4 space-y-2 text-sm text-zinc-400">
              <li><span>Luanda, Angola</span></li>
              <li><span>suporte@lumora.ao</span></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Legal</p>
            <ul className="mt-4 space-y-2 text-sm text-zinc-400">
              <li><span>Termos de Utilização</span></li>
              <li><span>Política de Privacidade</span></li>
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-12 max-w-7xl border-t border-white/10 px-6 pt-6 text-sm text-zinc-600">
          © 2026 Lumora · Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
}
